/**
 * Async thunks for PackingV2
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { message } from 'antd';
import moment from 'moment';
import { emitPackingPerf, extractErrorMessage, getShipmentPayloadStats, nowMs, runFastMutationRefresh, scheduleDebouncedFullShipmentRefresh } from './packingThunkUtils';
// Helper to get auth headers
var getAuthHeaders = function () {
    var moquiSessionToken = localStorage.getItem('moquiSessionToken');
    return moquiSessionToken ? { moquiSessionToken: moquiSessionToken } : {};
};
var getScaleComputerIdFromHeaders = function (headers) {
    var _a;
    return ((headers === null || headers === void 0 ? void 0 : headers['x-scale-computerid']) ||
        (headers === null || headers === void 0 ? void 0 : headers['X-Scale-ComputerId']) ||
        ((_a = headers === null || headers === void 0 ? void 0 : headers.get) === null || _a === void 0 ? void 0 : _a.call(headers, 'X-Scale-ComputerId')) ||
        'Not returned');
};
var getPackingQzHostname = function (state) { return state.packing.qzHostname; };
// Shared post-mutation strategy:
// 1) refresh package/item-asset slices immediately for responsiveness
// 2) debounce a silent full shipment refresh for eventual consistency
var refreshShipmentAfterMutation = function (dispatch, shipmentId, mutation, forceFullRefresh) {
    if (forceFullRefresh === void 0) { forceFullRefresh = false; }
    return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!forceFullRefresh) return [3 /*break*/, 2];
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId, silent: true }))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
                case 2: return [4 /*yield*/, runFastMutationRefresh({
                        shipmentId: shipmentId,
                        mutation: mutation,
                        performFastRefresh: function () { return __awaiter(void 0, void 0, void 0, function () {
                            var _a, packagesResult, itemAssetsResult;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0: return [4 /*yield*/, Promise.all([
                                            dispatch(fetchShipmentPackages({ shipmentId: shipmentId })),
                                            dispatch(fetchShipmentItemAssets({ shipmentId: shipmentId }))
                                        ])];
                                    case 1:
                                        _a = _b.sent(), packagesResult = _a[0], itemAssetsResult = _a[1];
                                        return [2 /*return*/, { packagesResult: packagesResult, itemAssetsResult: itemAssetsResult }];
                                }
                            });
                        }); },
                        scheduleFollowUpRefresh: function () {
                            scheduleDebouncedFullShipmentRefresh({
                                shipmentId: shipmentId,
                                reason: mutation,
                                // Silent refresh updates data without flipping global shipment loading state.
                                performRefresh: function () { return dispatch(fetchShipmentDetails({ shipmentId: shipmentId, silent: true })); }
                            });
                        }
                    })];
                case 3:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
};
// Fetch shipment details
/**
 * Endpoint: /rest/s1/handheld/shipment/${shipmentId}
 * Calls fp.ShipmentServices.get#ShipmentDetails -- As of Feb 1 2026 (subject to change, always verify)
 * Refactored from: getPackingShipmentDetails (auth/getShipmentDetails) thunk
 */
export var fetchShipmentDetails = createAsyncThunk('packing/fetchShipmentDetails', function (_a, _b) {
    var shipmentId = _a.shipmentId, _c = _a.silent, silent = _c === void 0 ? false : _c;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var requestStartedAt, activeOrgId, response, data_1, assemblyProducts, kitComponentProduct, uniqueKitComponentProduct, shipmentData, packageMetadataBySeqId, error_1, errorMsg;
        var _d, _e, _f, _g, _h, _j, _k, _l;
        return __generator(this, function (_m) {
            switch (_m.label) {
                case 0:
                    requestStartedAt = nowMs();
                    emitPackingPerf('shipment_details.request.started', { shipmentId: shipmentId, silent: silent });
                    _m.label = 1;
                case 1:
                    _m.trys.push([1, 6, , 7]);
                    activeOrgId = localStorage.getItem('activeOrgId');
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/shipment/" + shipmentId + "?isOutgoing=true&partyId=" + activeOrgId + "&includeContainers=true", { headers: getAuthHeaders() })];
                case 2:
                    response = _m.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 5];
                    data_1 = response.data;
                    assemblyProducts = (_e = (_d = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _d === void 0 ? void 0 : _d.produceWepList) === null || _e === void 0 ? void 0 : _e.map(function (item) {
                        var _a, _b, _c, _d, _e;
                        var product = (_a = data_1 === null || data_1 === void 0 ? void 0 : data_1.shipmentItemDetailList) === null || _a === void 0 ? void 0 : _a.find(function (pro) { return (pro === null || pro === void 0 ? void 0 : pro.productId) === (item === null || item === void 0 ? void 0 : item.productId); });
                        return __assign(__assign({}, item), { productName: (_b = product === null || product === void 0 ? void 0 : product.productName) !== null && _b !== void 0 ? _b : '', completed: (_e = (_d = (_c = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _c === void 0 ? void 0 : _c.assemblyReceivedByProduct) === null || _d === void 0 ? void 0 : _d[item === null || item === void 0 ? void 0 : item.productId]) !== null && _e !== void 0 ? _e : 0 });
                    });
                    kitComponentProduct = (_f = data_1 === null || data_1 === void 0 ? void 0 : data_1.componentProductList) === null || _f === void 0 ? void 0 : _f.map(function (item) {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
                        var totalQuantity = (_b = (_a = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _a === void 0 ? void 0 : _a.consumeWepList) === null || _b === void 0 ? void 0 : _b.find(function (qty) { return (qty === null || qty === void 0 ? void 0 : qty.productId) === (item === null || item === void 0 ? void 0 : item.componentProductId); });
                        return __assign(__assign({}, item), { totalQty: (_c = item === null || item === void 0 ? void 0 : item.totalQuantity) !== null && _c !== void 0 ? _c : 0, quantityNotIssued: (_d = item === null || item === void 0 ? void 0 : item.quantityNotIssued) !== null && _d !== void 0 ? _d : 0, quantity: totalQuantity === null || totalQuantity === void 0 ? void 0 : totalQuantity.estimatedQuantity, quantityTotal: (_g = (_f = (_e = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _e === void 0 ? void 0 : _e.componentIssuedQtyByProduct) === null || _f === void 0 ? void 0 : _f[item === null || item === void 0 ? void 0 : item.componentProductId]) !== null && _g !== void 0 ? _g : 0, usedQuantity: (_k = (_j = (_h = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _h === void 0 ? void 0 : _h.componentUsedQtyByProduct) === null || _j === void 0 ? void 0 : _j[item === null || item === void 0 ? void 0 : item.componentProductId]) !== null && _k !== void 0 ? _k : 0, unUsedQuantity: (_o = (_m = (_l = data_1 === null || data_1 === void 0 ? void 0 : data_1.assemblyAssetInfo) === null || _l === void 0 ? void 0 : _l.componentIssuedAvailable) === null || _m === void 0 ? void 0 : _m[item === null || item === void 0 ? void 0 : item.componentProductId]) !== null && _o !== void 0 ? _o : 0, packDate: (_q = (_p = data_1 === null || data_1 === void 0 ? void 0 : data_1.shipment) === null || _p === void 0 ? void 0 : _p.estimatedShipDate) !== null && _q !== void 0 ? _q : '' });
                    });
                    uniqueKitComponentProduct = kitComponentProduct
                        ? Object.values(kitComponentProduct.reduce(function (acc, item) {
                            var key = item.componentProductId + "-" + item.orderItemSeqId;
                            if (!acc[key]) {
                                acc[key] = item;
                            }
                            return acc;
                        }, {}))
                        : [];
                    shipmentData = __assign(__assign({}, data_1), { assemblyProducts: assemblyProducts, kitComponentProduct: uniqueKitComponentProduct });
                    packageMetadataBySeqId = ((_g = data_1 === null || data_1 === void 0 ? void 0 : data_1.shipmentPackageList) !== null && _g !== void 0 ? _g : []).reduce(function (group, pkg) {
                        var _a;
                        var shipmentPackageSeqId = pkg.shipmentPackageSeqId;
                        group[shipmentPackageSeqId] = (_a = group[shipmentPackageSeqId]) !== null && _a !== void 0 ? _a : [];
                        group[shipmentPackageSeqId].push(pkg);
                        return group;
                    }, {});
                    // Dispatch to fetch packages
                    return [4 /*yield*/, dispatch(fetchShipmentPackages({ shipmentId: shipmentId })).unwrap()
                        // Fetch item assets for unpacking
                    ];
                case 3:
                    // Dispatch to fetch packages
                    _m.sent();
                    // Fetch item assets for unpacking
                    return [4 /*yield*/, dispatch(fetchShipmentItemAssets({ shipmentId: shipmentId })).unwrap()
                        // Fetch attachments if order exists
                    ];
                case 4:
                    // Fetch item assets for unpacking
                    _m.sent();
                    // Fetch attachments if order exists
                    if ((_j = (_h = data_1 === null || data_1 === void 0 ? void 0 : data_1.shipmentItemSourceList) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.orderId) {
                        dispatch(fetchAttachments({ orderId: data_1.shipmentItemSourceList[0].orderId }));
                    }
                    emitPackingPerf('shipment_details.request.completed', __assign({ shipmentId: shipmentId, silent: silent, durationMs: Number((nowMs() - requestStartedAt).toFixed(1)) }, getShipmentPayloadStats(data_1)));
                    return [2 /*return*/, {
                            shipmentData: shipmentData,
                            packageMetadataBySeqId: packageMetadataBySeqId
                        }];
                case 5:
                    emitPackingPerf('shipment_details.request.empty', {
                        shipmentId: shipmentId,
                        silent: silent,
                        durationMs: Number((nowMs() - requestStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, null];
                case 6:
                    error_1 = _m.sent();
                    errorMsg = ((_l = (_k = error_1.response) === null || _k === void 0 ? void 0 : _k.data) === null || _l === void 0 ? void 0 : _l.errors) || 'Failed to fetch shipment details';
                    emitPackingPerf('shipment_details.request.failed', {
                        shipmentId: shipmentId,
                        silent: silent,
                        durationMs: Number((nowMs() - requestStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 7: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchShipmentPackages
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/package
// Service: handheld.shipment.ShipmentServices.get#ShipmentPackage
// Refactored from: getShipmentPackage (auth/getShipmentPackage) thunk
// =============================================================================
export var fetchShipmentPackages = createAsyncThunk('packing/fetchShipmentPackages', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, contentList, packageContentBySeqId, orderItemList, packageOrderItemsBySeqId, serialNumberList, packageSerialNumbersBySeqId, containerPackerById, error_2, errorMsg;
        var _c, _d, _e, _f, _g;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    _h.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _h.sent();
                    if (response.status === 200) {
                        data = response.data;
                        contentList = (_c = data === null || data === void 0 ? void 0 : data.shipmentPackageContentList) !== null && _c !== void 0 ? _c : [];
                        packageContentBySeqId = contentList.reduce(function (acc, item) {
                            var _a;
                            var shipmentPackageSeqId = item.shipmentPackageSeqId;
                            acc[shipmentPackageSeqId] = (_a = acc[shipmentPackageSeqId]) !== null && _a !== void 0 ? _a : [];
                            acc[shipmentPackageSeqId].push(item);
                            return acc;
                        }, {});
                        orderItemList = (_d = data === null || data === void 0 ? void 0 : data.ShipmentPackageOrderItemList) !== null && _d !== void 0 ? _d : [];
                        packageOrderItemsBySeqId = orderItemList.reduce(function (acc, item) {
                            var _a;
                            var shipmentPackageSeqId = item.shipmentPackageSeqId;
                            acc[shipmentPackageSeqId] = (_a = acc[shipmentPackageSeqId]) !== null && _a !== void 0 ? _a : [];
                            acc[shipmentPackageSeqId].push(item);
                            return acc;
                        }, {});
                        serialNumberList = (_e = data === null || data === void 0 ? void 0 : data.ShipmentPackageContentSerialNumberList) !== null && _e !== void 0 ? _e : [];
                        packageSerialNumbersBySeqId = serialNumberList.reduce(function (acc, item) {
                            var _a;
                            var shipmentPackageSeqId = item.shipmentPackageSeqId;
                            acc[shipmentPackageSeqId] = (_a = acc[shipmentPackageSeqId]) !== null && _a !== void 0 ? _a : [];
                            acc[shipmentPackageSeqId].push(item);
                            return acc;
                        }, {});
                        containerPackerById = ((data === null || data === void 0 ? void 0 : data.shipmentContainerPackerList) || []).reduce(function (acc, item) {
                            var containerId = item.containerId;
                            acc[containerId] = item !== null && item !== void 0 ? item : null;
                            return acc;
                        }, {});
                        return [2 /*return*/, {
                                packageContentBySeqId: packageContentBySeqId,
                                packageOrderItemsBySeqId: packageOrderItemsBySeqId,
                                packageSerialNumbersBySeqId: packageSerialNumbersBySeqId,
                                containerPackerById: containerPackerById
                            }];
                    }
                    return [2 /*return*/, null];
                case 2:
                    error_2 = _h.sent();
                    errorMsg = ((_g = (_f = error_2.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.errors) || 'Failed to fetch shipment packages';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// scanProductForPacking
// GET /rest/s1/handheld/facility/${facilityId}/scanIdGeneral
// Service: handheld.FacilityServices.get#HHScannedIdGeneralCaseInsensitive
// Validates scanned product belongs to shipment, then dispatches packItem
// Refactored from: getScannedProductforPacking (auth/getScannedProductforPacking) thunk
// =============================================================================
export var scanProductForPacking = createAsyncThunk('packing/scanProductForPacking', function (_a, _b) {
    var scanId = _a.scanId, shipmentId = _a.shipmentId, shipmentItemDetailList = _a.shipmentItemDetailList, kitComponentProducts = _a.kitComponentProducts, shipmentPackageSeqId = _a.shipmentPackageSeqId, shipmentBoxTypeId = _a.shipmentBoxTypeId, _c = _a.quantity, quantity = _c === void 0 ? 1 : _c, carton = _a.carton, serialNumber = _a.serialNumber;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var facilityId, ownerPartyId, response, data, scannedProductId_1, scannedPseudoId_1, matchedItem, matchedKitComponent, packResult, packResult, errorMsg, error_3, errorMsg;
        var _d, _e, _f;
        return __generator(this, function (_g) {
            switch (_g.label) {
                case 0:
                    _g.trys.push([0, 7, , 8]);
                    facilityId = localStorage.getItem('facilityId');
                    ownerPartyId = localStorage.getItem('activeOrgId');
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/facility/" + facilityId + "/scanIdGeneral?scanId=" + encodeURIComponent(scanId) + "&ownerPartyId=" + ownerPartyId, { headers: getAuthHeaders() })];
                case 1:
                    response = _g.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 6];
                    data = response.data;
                    scannedProductId_1 = data === null || data === void 0 ? void 0 : data.productId;
                    scannedPseudoId_1 = (_d = data === null || data === void 0 ? void 0 : data.product) === null || _d === void 0 ? void 0 : _d.pseudoId;
                    matchedItem = shipmentItemDetailList === null || shipmentItemDetailList === void 0 ? void 0 : shipmentItemDetailList.find(function (item) { return item.productId === scannedProductId_1 || item.pseudoId === scannedPseudoId_1; });
                    matchedKitComponent = kitComponentProducts === null || kitComponentProducts === void 0 ? void 0 : kitComponentProducts.find(function (item) { return item.componentProductId === scannedProductId_1; });
                    if (!matchedKitComponent) return [3 /*break*/, 3];
                    return [4 /*yield*/, dispatch(packItem({
                            shipmentId: shipmentId,
                            scanInput: scanId,
                            quantity: quantity,
                            shipmentPackageSeqId: shipmentPackageSeqId,
                            shipmentBoxTypeId: shipmentBoxTypeId,
                            carton: carton,
                            serialNumber: serialNumber
                        })
                        // packKitItem({
                        //   shipmentId,
                        //   productId: matchedKitComponent.componentProductId,
                        //   quantity,
                        //   shipmentPackageSeqId,
                        //   assemblyProductId: matchedKitComponent.kitProductId
                        // })
                        ).unwrap()];
                case 2:
                    packResult = _g.sent();
                    return [2 /*return*/, {
                            matched: true,
                            type: 'kit',
                            productId: scannedProductId_1,
                            packResult: packResult,
                        }];
                case 3:
                    if (!matchedItem) return [3 /*break*/, 5];
                    return [4 /*yield*/, dispatch(packItem({
                            shipmentId: shipmentId,
                            scanInput: scanId,
                            quantity: quantity,
                            shipmentPackageSeqId: shipmentPackageSeqId,
                            shipmentBoxTypeId: shipmentBoxTypeId,
                            carton: carton,
                            serialNumber: serialNumber
                        })).unwrap()];
                case 4:
                    packResult = _g.sent();
                    return [2 /*return*/, {
                            matched: true,
                            type: 'item',
                            productId: scannedProductId_1,
                            packResult: packResult,
                        }];
                case 5:
                    errorMsg = 'Incorrect product, Scan again';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/, rejectWithValue('Failed to scan product')];
                case 7:
                    error_3 = _g.sent();
                    errorMsg = ((_f = (_e = error_3.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.errors) || 'Failed to scan product';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 8: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// packItem
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/package/
// Service: handheld.shipment.ShipmentServices.pack#ShipmentProductScan
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/package/bulkPackage (when carton is provided)
// Service: handheld.shipment.ShipmentServices.bulkPack#ShipmentProductScan
// Refactored from: packItem (auth/packItem) thunk & shipmentPackItem (auth/shipmentPackItem) thunk
// =============================================================================
export var packItem = createAsyncThunk('packing/packItem', function (_a, _b) {
    var shipmentId = _a.shipmentId, scanInput = _a.scanInput, quantity = _a.quantity, shipmentPackageSeqId = _a.shipmentPackageSeqId, shipmentBoxTypeId = _a.shipmentBoxTypeId, carton = _a.carton, serialNumber = _a.serialNumber;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, endpoint, response, error_4, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'packItem',
                        shipmentId: shipmentId,
                        hasCarton: Boolean(carton)
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    endpoint = carton
                        ? "/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/bulkPackage"
                        : "/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/";
                    return [4 /*yield*/, axios.post(endpoint, __assign(__assign(__assign({ scanInput: scanInput, quantity: quantity, shipmentPackageSeqId: shipmentPackageSeqId || undefined }, (shipmentBoxTypeId && { shipmentBoxTypeId: shipmentBoxTypeId })), (carton && { carton: carton })), (serialNumber && { serialNumber: serialNumber })), { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Item packed successfully');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, shipmentId, 'packItem', !shipmentPackageSeqId)];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'packItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        endpoint: endpoint
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_4 = _c.sent();
                    errorMsg = extractErrorMessage(error_4, 'Failed to pack item');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'packItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// packItems
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/packItem
// Service: mantle.shipment.ShipmentServices.pack#ShipmentItemSource
// Refactored from: packItems (auth/packItems) thunk
// =============================================================================
export var packItemBySourceId = createAsyncThunk('packing/packItemBySourceId', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentItemSourceId = _a.shipmentItemSourceId, quantity = _a.quantity, shipmentPackageSeqId = _a.shipmentPackageSeqId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_5, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'packItemBySourceId',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/packItem", {
                            shipmentItemSourceId: shipmentItemSourceId,
                            quantity: quantity,
                            shipmentPackageSeqId: shipmentPackageSeqId
                        }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Item packed successfully');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, shipmentId, 'packItemBySourceId', !shipmentPackageSeqId)];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'packItemBySourceId',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_5 = _c.sent();
                    errorMsg = extractErrorMessage(error_5, 'Failed to pack item');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'packItemBySourceId',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
export var packItemWithSerial = createAsyncThunk('packing/packItemWithSerial', function (data, _a) {
    var rejectWithValue = _a.rejectWithValue, dispatch = _a.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, endpoint, response, resData, error_6, errorMsg;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'packItemWithSerial',
                        shipmentId: data.shipmentId,
                        hasCarton: Boolean(data.carton)
                    });
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 5, , 6]);
                    endpoint = data.carton
                        ? "/rest/s1/handheld/pack/shipment/" + data.shipmentId + "/package/bulkPackage"
                        : "/rest/s1/handheld/pack/shipment/" + data.shipmentId + "/package/";
                    return [4 /*yield*/, axios.post(endpoint, data, { headers: getAuthHeaders() })];
                case 2:
                    response = _b.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    resData = response.data;
                    if ((resData === null || resData === void 0 ? void 0 : resData.messages) && resData.error) {
                        message.error(resData.messages);
                        return [2 /*return*/, rejectWithValue(resData.messages)];
                    }
                    message.success('Shipment Pack Item Successfully.');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, data.shipmentId, 'packItemWithSerial', !data.shipmentPackageSeqId)];
                case 3:
                    _b.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'packItemWithSerial',
                        shipmentId: data.shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        endpoint: endpoint
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_6 = _b.sent();
                    errorMsg = extractErrorMessage(error_6, 'Failed to pack item');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'packItemWithSerial',
                        shipmentId: data.shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// packKitItem
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/packKitItem
// Service: mantle.shipment.ShipmentServices.pack#ShipmentAssemblyComponent
// Refactored from: packKitItems (auth/packItems) thunk
// =============================================================================
export var packKitItem = createAsyncThunk('packing/packKitItem', function (_a, _b) {
    var shipmentId = _a.shipmentId, productId = _a.productId, quantity = _a.quantity, shipmentPackageSeqId = _a.shipmentPackageSeqId, assemblyProductId = _a.assemblyProductId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_7, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'packKitItem',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/packKitItem", { productId: productId, quantity: quantity, shipmentPackageSeqId: shipmentPackageSeqId, assemblyProductId: assemblyProductId }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Kit item packed successfully');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, shipmentId, 'packKitItem', !shipmentPackageSeqId)];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'packKitItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_7 = _c.sent();
                    errorMsg = extractErrorMessage(error_7, 'Failed to pack kit item');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'packKitItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// unpackItem
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/unPackItem
// Service: mantle.shipment.ShipmentServices.unpack#ShipmentItemIssuance
// =============================================================================
export var unpackItem = createAsyncThunk('packing/unpackItem', function (_a, _b) {
    var shipmentId = _a.shipmentId, assetIssuanceId = _a.assetIssuanceId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_8, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'unpackItem',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/unPackItem", { assetIssuanceId: assetIssuanceId }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Item unpacked successfully');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, shipmentId, 'unpackItem')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'unpackItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_8 = _c.sent();
                    errorMsg = extractErrorMessage(error_8, 'Failed to unpack item');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'unpackItem',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// closePackage
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}/close
// Service: mantle.shipment.ShipmentServices.update#ShipmentPackageAndRouteSeg
// Refactored from: closeShipmentPackage (auth/closeShipmentPackage)
// =============================================================================
export var closePackage = createAsyncThunk('packing/closePackage', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId, hostname = _a.hostname;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch, getState = _b.getState;
    return __awaiter(void 0, void 0, void 0, function () {
        var resolvedHostname, response, hostnameMessage, error_9, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    resolvedHostname = hostname !== null && hostname !== void 0 ? hostname : getPackingQzHostname(getState());
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId + "/close", __assign({ shipmentId: shipmentId, shipmentPackageSeqId: shipmentPackageSeqId, shipmentRouteSegmentSeqId: '01', shipmentBoxTypeId: shipmentBoxTypeId, weight: weight, weightUomId: weightUomId }, (resolvedHostname ? { hostname: resolvedHostname } : {})), { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    hostnameMessage = resolvedHostname
                        ? "Workstation hostname sent: " + resolvedHostname + "."
                        : 'No workstation hostname was detected, so no hostname was sent.';
                    message.success("Shipment package closed successfully. " + hostnameMessage);
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_9 = _e.sent();
                    errorMsg = ((_d = (_c = error_9.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to close package';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// closeBulkPackage
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/package/bulkPackage
// Service: handheld.shipment.ShipmentServices.update#ShipmentBulkPackageAndRouteSeg
// Refactored from: CloseBulkPackage (auth/CloseBulkPackage)
// =============================================================================
export var closeBulkPackage = createAsyncThunk('packing/closeBulkPackage', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId, carton = _a.carton, shipmentPackageSeqIds = _a.shipmentPackageSeqIds;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_10, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/bulkPackage", {
                            shipmentId: shipmentId,
                            shipmentBoxTypeId: shipmentBoxTypeId,
                            weight: weight,
                            weightUomId: weightUomId,
                            shipmentPackageSeqIds: shipmentPackageSeqIds
                            // carton
                        }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Bulk packages closed successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_10 = _e.sent();
                    errorMsg = ((_d = (_c = error_10.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to close bulk packages';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// deletePackage
// DELETE /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}
// Service: mantle.shipment.ShipmentServices.delete#ShipmentPackage
// Refactored from: deletePackage (auth/deletePackage) thunk
// =============================================================================
export var deletePackage = createAsyncThunk('packing/deletePackage', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_11, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.delete("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Package deleted successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_11 = _e.sent();
                    errorMsg = ((_d = (_c = error_11.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to delete package';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// resetPackage
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}/reset
// Service: mantle.shipment.ShipmentServices.reset#ShipmentPackage
// Refactored from: ResetPackage (auth/ResetPackage) thunk
// =============================================================================
export var resetPackage = createAsyncThunk('packing/resetPackage', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_12, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId + "/reset", { shipmentId: shipmentId, shipmentPackageSeqId: shipmentPackageSeqId }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Shipment package reset successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_12 = _e.sent();
                    errorMsg = ((_d = (_c = error_12.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to reset package';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// resetAllPackages
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/resetAllPackages
// Service: mantle.shipment.ShipmentServices.reset#AllShipmentPackages
// Refactored from: ResetAllPackage (auth/ResetAllPackage) thunk
// =============================================================================
export var resetAllPackages = createAsyncThunk('packing/resetAllPackages', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_13, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/resetAllPackages", { shipmentId: shipmentId }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('All packages reset successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_13 = _e.sent();
                    errorMsg = ((_d = (_c = error_13.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to reset all packages';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// deleteAllPackages
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/deleteAllPackages
// Service: mantle.shipment.ShipmentServices.delete#AllShipmentPackages
// Refactored from: deleteAllPackage (auth/deleteAllPackage) thunk
// =============================================================================
export var deleteAllPackages = createAsyncThunk('packing/deleteAllPackages', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_14, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/deleteAllPackages", { shipmentId: shipmentId }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('All packages deleted successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_14 = _e.sent();
                    errorMsg = ((_d = (_c = error_14.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to delete all packages';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// updatePackage
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}
// Service: mantle.shipment.ShipmentServices.update#ShipmentPackageAndRouteSeg
// Refactored from: getUpdatePackage (auth/getUpdatePackage) thunk
// =============================================================================
export var updatePackage = createAsyncThunk('packing/updatePackage', function (_a, _b) {
    var _c, _d;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_15, errorMsg;
        var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, data = __rest(_a, ["shipmentId", "shipmentPackageSeqId"]);
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId, __assign({ shipmentId: shipmentId, shipmentPackageSeqId: shipmentPackageSeqId }, data), { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Package updated successfully.');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_15 = _e.sent();
                    errorMsg = ((_d = (_c = error_15.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to update package';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// markShipmentPacked
// POST /rest/s1/handheld/pack/shipment/${shipmentId}/packComplete
// Service: mantle.shipment.ShipmentServices.complete#ShipmentPack
// Refactored from: markShipmentPacked (auth/markShipmentPacked) thunk
// =============================================================================
export var markShipmentPacked = createAsyncThunk('packing/markShipmentPacked', function (_a, _b) {
    var _c, _d;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, resData, error_16, errorMsg;
        var shipmentId = _a.shipmentId, serialized = _a.serialized, data = __rest(_a, ["shipmentId", "serialized"]);
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/packComplete", data, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    resData = response.data;
                    // TODO:
                    // - Note: This follows the exact same sequence as the original thunk
                    // I'm not sure if this is the best way to handle this
                    // Need more information about the cases. For example why is there
                    // an error when we have messages other than 'already has label?
                    // - Print handling was moved to component
                    if (resData === null || resData === void 0 ? void 0 : resData.messages) {
                        if (resData.messages.includes('already has label')) {
                            message.warning(resData.messages);
                            if (serialized) {
                                message.warning('You have packed serialized product, regenerate the packing slip.');
                            }
                        }
                        else {
                            message.error(resData.messages);
                            return [2 /*return*/, rejectWithValue(resData.messages)];
                        }
                    }
                    else {
                        if (serialized) {
                            message.warning('You have packed serialized product, regenerate the packing slip.');
                        }
                        else {
                            message.success('Shipment marked as packed successfully.');
                        }
                    }
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_16 = _e.sent();
                    errorMsg = ((_d = (_c = error_16.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to mark shipment as packed';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// markShipmentShipped
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/setShipped
// Service: mantle.shipment.ShipmentServices.ship#Shipment
// Refactored from: markShipmentShipped (auth/markShipmentShipped) thunk
// =============================================================================
export var markShipmentShipped = createAsyncThunk('packing/markShipmentShipped', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_17, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/setShipped", { actualStartDate: moment().format('YYYY-MM-DD') }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        message.success('Shipment marked as shipped!');
                        return [2 /*return*/, response.data];
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_17 = _e.sent();
                    errorMsg = ((_d = (_c = error_17.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to mark shipment as shipped';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchBoxTypes
// GET /rest/s1/handheld/facility/${facilityId}/boxType
// Service: fp.FacilityServices.get#FacilityBoxType
// Refactored from: getFacilityBoxType (auth/getFacilityBoxType) thunk
// =============================================================================
export var fetchBoxTypes = createAsyncThunk('packing/fetchBoxTypes', function (_, _a) {
    var rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var facilityId, response, data, error_18, errorMsg;
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 2, , 3]);
                    facilityId = localStorage.getItem('facilityId');
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/facility/" + facilityId + "/boxType", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _d.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.boxTypeList.map(function (item) { return ({
                                key: item.shipmentBoxTypeId,
                                value: (item === null || item === void 0 ? void 0 : item.description) || (item === null || item === void 0 ? void 0 : item.pseudoId) || (item === null || item === void 0 ? void 0 : item.shipmentBoxTypeId) || ''
                            }); })];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_18 = _d.sent();
                    errorMsg = ((_c = (_b = error_18.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.errors) || 'Failed to fetch box types';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchWeightUoMs
// GET /rest/s1/fp/uom?uomTypeEnumId=UT_WEIGHT_MEASURE
// Service: utility.UtilityServices.get#WeightUomList
// Refactored from: getWeightUoM (auth/getWeightUoM) thunk
// =============================================================================
export var fetchWeightUoMs = createAsyncThunk('packing/fetchWeightUoMs', function (_, _a) {
    var rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_19, errorMsg;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get('/rest/s1/fp/uom?uomTypeEnumId=UT_WEIGHT_MEASURE', {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, (((_b = data.uomList) === null || _b === void 0 ? void 0 : _b.map(function (item) { return ({
                                key: item.uomId,
                                value: item.description || item.abbreviation || item.uomId
                            }); })) || [])];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_19 = _e.sent();
                    errorMsg = ((_d = (_c = error_19.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch weight UoMs';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchShippingMethods
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/shippingOptions?getRates=true
// Service: mantle.shipment.ShipmentServices.get#ShipmentShippingOptions
// Refactored from: getShippingMethods (auth/getShippingMethods) thunk
// =============================================================================
export var fetchShippingMethods = createAsyncThunk('packing/fetchShippingMethods', function (_a, _b) {
    var shipmentId = _a.shipmentId, _c = _a.getRates, getRates = _c === void 0 ? true : _c;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_20, errorMsg;
        var _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/shippingOptions?getRates=" + getRates, {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _f.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.shippingOptions || []];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_20 = _f.sent();
                    errorMsg = ((_e = (_d = error_20.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.errors) || 'Failed to fetch shipping methods';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchShipmentList (From Bin/Container Scan)
// GET /rest/s1/handheld/pack/containerShipments
// Service: handheld.shipment.ShipmentServices.get#ContainerShipments
// Refactored from: getShipmentList (auth/getShipmentList) thunk
// =============================================================================
export var fetchShipmentList = createAsyncThunk('packing/fetchShipmentList', function (_a, _b) {
    var containerId = _a.containerId, _c = _a.pageIndex, pageIndex = _c === void 0 ? 0 : _c;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_21, errorMsg;
        var _d, _e, _f;
        return __generator(this, function (_g) {
            switch (_g.label) {
                case 0:
                    _g.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/containerShipments?containerId=" + containerId + "&pageSize=10&pageIndex=" + pageIndex, { headers: getAuthHeaders() })];
                case 1:
                    response = _g.sent();
                    data = response.data;
                    if (response.status === 200 && ((_d = data === null || data === void 0 ? void 0 : data.shipmentsList) === null || _d === void 0 ? void 0 : _d.length) > 0) {
                        return [2 /*return*/, {
                                shipmentsList: data.shipmentsList,
                                pageMaxIndex: data.shipmentsListPageMaxIndex + 1,
                                recordCount: data.shipmentsListCount
                            }];
                    }
                    message.error('No record found');
                    return [2 /*return*/, null];
                case 2:
                    error_21 = _g.sent();
                    errorMsg = ((_f = (_e = error_21.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.errors) || 'Failed to fetch shipment list';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchAttachments
// GET /rest/s1/handheld/attachments?orderId=${orderId}
// Service: fp.OrderServices.get#OrderContent
// Refactored from: getAttachmentList (auth/getAttachmentList) thunk
// =============================================================================
export var fetchAttachments = createAsyncThunk('packing/fetchAttachments', function (_a, _b) {
    var orderId = _a.orderId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_22, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/attachments?orderId=" + orderId, {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.contentList || []];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_22 = _e.sent();
                    errorMsg = ((_d = (_c = error_22.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch attachments';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchCountries
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/geos
// Service: fp.AccountServices.get#Geos
// Refactored from: getCountriesList (auth/getCountriesList) thunk
// =============================================================================
export var fetchCountries = createAsyncThunk('packing/fetchCountries', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_23, errorMsg;
        var _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/geos", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _f.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, (((_c = data.geoList) === null || _c === void 0 ? void 0 : _c.map(function (geo) { return ({
                                key: geo.geoId,
                                value: geo.geoName
                            }); })) || [])];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_23 = _f.sent();
                    errorMsg = ((_e = (_d = error_23.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.errors) || 'Failed to fetch countries';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchStates
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/geos/${countryGeoId}/regions
// Service: org.moqui.impl.BasicServices.get#GeoRegionsForDropDown
// Refactored from: getStatesList (auth/getStatesList) thunk
// =============================================================================
export var fetchStates = createAsyncThunk('packing/fetchStates', function (_a, _b) {
    var shipmentId = _a.shipmentId, countryGeoId = _a.countryGeoId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_24, errorMsg;
        var _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/geos/" + countryGeoId + "/regions", { headers: getAuthHeaders() })];
                case 1:
                    response = _f.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, (((_c = data.resultList) === null || _c === void 0 ? void 0 : _c.map(function (geo) { return ({
                                key: geo.geoId,
                                value: geo.geoName
                            }); })) || [])];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_24 = _f.sent();
                    errorMsg = ((_e = (_d = error_24.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.errors) || 'Failed to fetch states';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// updateAddress
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/address
// Service: mantle.party.ContactServices.store#PartyContactInfo
// Refactored from: UpdateAddress (auth/UpdateAddress) thunk
// =============================================================================
export var updateAddress = createAsyncThunk('packing/updateAddress', function (_a, _b) {
    var _c, _d;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var moquiSessionToken, response, error_25, errorMsg;
        var shipmentId = _a.shipmentId, data = __rest(_a, ["shipmentId"]);
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    moquiSessionToken = localStorage.getItem('moquiSessionToken');
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/address?moquiSessionToken=" + moquiSessionToken, __assign({ shipmentId: shipmentId }, data), { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Shipment Address Updated Successfully');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_25 = _e.sent();
                    errorMsg = ((_d = (_c = error_25.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to update address';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// getReturnLabel
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}/returnLabel
// Service: mantle.shipment.CarrierServices.request#ShipmentLabels
// Refactored from: getReturnLabel (auth/getReturnLabel) thunk
// =============================================================================
export var getReturnLabel = createAsyncThunk('packing/getReturnLabel', function (_a, _b) {
    var _c, _d;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_26, errorMsg;
        var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, data = __rest(_a, ["shipmentId", "shipmentPackageSeqId"]);
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId + "/returnLabel", { params: __assign({ getReturnLabels: true }, data), headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Return label generated successfully');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_26 = _e.sent();
                    errorMsg = ((_d = (_c = error_26.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to get return label';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// getMainLabel
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}/returnLabel
// Service: mantle.shipment.CarrierServices.request#ShipmentLabels
// Refactored from: getLabel (auth/getLabel) thunk
// TODO: check if this is the same as getReturnLabel
// =============================================================================
export var getMainLabel = createAsyncThunk('packing/getMainLabel', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, shipmentRouteSegmentSeqId = _a.shipmentRouteSegmentSeqId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, resData, error_27, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 6, , 7]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId + "/returnLabel", { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 5];
                    resData = response.data;
                    if (!(resData === null || resData === void 0 ? void 0 : resData.messages)) return [3 /*break*/, 2];
                    message.success(resData.messages);
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 3:
                    _e.sent();
                    _e.label = 4;
                case 4: return [2 /*return*/, resData];
                case 5: return [3 /*break*/, 7];
                case 6:
                    error_27 = _e.sent();
                    errorMsg = ((_d = (_c = error_27.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to get label';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 7: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// getCartonLabel
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/spsCartonLabel
// Service: handheld.shipment.ShipmentServices.get#SPSCartonLabel
// Refactored from: getCartonLabel (auth/getCartonLabel) thunk
// =============================================================================
export var getCartonLabel = createAsyncThunk('packing/getCartonLabel', function (_a, _b) {
    var shipmentId = _a.shipmentId, productStoreId = _a.productStoreId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_28, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/spsCartonLabel?shipmentId=" + shipmentId + "&productStoreId=" + productStoreId, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        return [2 /*return*/, response.data];
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_28 = _e.sent();
                    errorMsg = ((_d = (_c = error_28.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to get carton label';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchUserPreferences
// GET /rest/s1/handheld/user/${userId}/preference
// Service: user.UserServices.get#UserPreferences
// Refactored from: getActiveUserPreference (auth/getActiveUserPreference) thunk
// =============================================================================
export var fetchUserPreferences = createAsyncThunk('packing/fetchUserPreferences', function (_, _a) {
    var rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, userPreferences_1, error_29, errorMsg;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/user/" + localStorage.getItem('userId') + "/preference", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        userPreferences_1 = {};
                        (_b = data === null || data === void 0 ? void 0 : data.userPreferenceList) === null || _b === void 0 ? void 0 : _b.forEach(function (u) {
                            if (['qz.print.enabled', 'qz.printer.label.active', 'qz.printer.main.active'].includes(u === null || u === void 0 ? void 0 : u.preferenceKey)) {
                                userPreferences_1[u.preferenceKey] = u.preferenceValue;
                            }
                        });
                        return [2 /*return*/, userPreferences_1];
                    }
                    return [2 /*return*/, {}];
                case 2:
                    error_29 = _e.sent();
                    errorMsg = ((_d = (_c = error_29.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch user preferences';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchDefaultNetworkPrinterDetail
// GET /rest/s1/handheld/user/${userId}/printer
// Hostname-aware default printer lookup used by PackingV2.
// =============================================================================
export var fetchDefaultNetworkPrinterDetail = createAsyncThunk('packing/fetchDefaultNetworkPrinterDetail', function (_a, _b) {
    var hostname = _a.hostname;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, printerName, preferences, error_30, errorMsg;
        var _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/user/" + localStorage.getItem('userId') + "/printer", {
                            headers: getAuthHeaders(),
                            params: hostname ? { hostname: hostname } : undefined
                        })];
                case 1:
                    response = _f.sent();
                    if (response.status === 200) {
                        data = response.data;
                        printerName = (_c = data === null || data === void 0 ? void 0 : data.defaultNetworkPrinterDetail) === null || _c === void 0 ? void 0 : _c.printerName;
                        preferences = printerName
                            ? { 'qz.printer.main.active': printerName }
                            : {};
                        return [2 /*return*/, preferences];
                    }
                    return [2 /*return*/, {}];
                case 2:
                    error_30 = _f.sent();
                    errorMsg = ((_e = (_d = error_30.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.errors) || 'Failed to fetch default printer';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchPrinterList
// GET /rest/s1/handheld/facility/${facilityPartyId}/printer
// Facility printer list used to map QZ printer names back to networkPrinterId.
// =============================================================================
export var fetchPrinterList = createAsyncThunk('packing/fetchPrinterList', function (_, _a) {
    var rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var facilityPartyId, response, data, printerList, error_31, errorMsg;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    facilityPartyId = localStorage.getItem('facilityId');
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/facility/" + facilityPartyId + "/printer", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        printerList = ((_b = data === null || data === void 0 ? void 0 : data.printerList) !== null && _b !== void 0 ? _b : []);
                        return [2 /*return*/, printerList
                                .filter(function (item) { return item.statusId === 'PsActive'; })
                                .map(function (item) { return ({
                                key: item.networkPrinterId,
                                value: item.printerName,
                                port: item === null || item === void 0 ? void 0 : item.serverPort,
                                host: item === null || item === void 0 ? void 0 : item.ipAddress
                            }); })];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_31 = _e.sent();
                    errorMsg = ((_d = (_c = error_31.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch printer list';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// setDefaultNetworkPrinterDetail
// PUT /rest/s1/handheld/user/${userId}/printer
// Hostname-aware default main printer save used by PackingV2.
// =============================================================================
export var setDefaultNetworkPrinterDetail = createAsyncThunk('packing/setDefaultNetworkPrinterDetail', function (_a, _b) {
    var networkPrinterId = _a.networkPrinterId, printerName = _a.printerName, hostname = _a.hostname;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_32, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/user/" + localStorage.getItem('userId') + "/printer", __assign({ networkPrinterId: networkPrinterId }, (hostname ? { hostname: hostname } : {})), { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        return [2 /*return*/, { 'qz.printer.main.active': printerName }];
                    }
                    return [2 /*return*/, rejectWithValue('Failed to set default printer')];
                case 2:
                    error_32 = _e.sent();
                    errorMsg = ((_d = (_c = error_32.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to set default printer';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// setUserPreference
// POST /rest/s1/handheld/user/${userId}/preference
// Service: user.UserServices.set#UserPreference
// Refactored from: setActiveUserPreference (auth/setActiveUserPreference) thunk
// =============================================================================
export var setUserPreference = createAsyncThunk('packing/setUserPreference', function (_a, _b) {
    var preferenceKey = _a.preferenceKey, preferenceValue = _a.preferenceValue;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_33, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/user/" + localStorage.getItem('userId') + "/preference", { preferenceKey: preferenceKey, preferenceValue: preferenceValue }, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        return [2 /*return*/, { key: preferenceKey, value: preferenceValue }];
                    }
                    return [2 /*return*/, null];
                case 2:
                    error_33 = _e.sent();
                    errorMsg = ((_d = (_c = error_33.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to set user preference';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchPackageWeight
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/weight
// Service: handheld.shipment.ShipmentServices.get#ShipmentPackageWeight
// Refactored from: getPackageWeight (auth/getPackageWeight) thunk
// =============================================================================
export var fetchPackageWeight = createAsyncThunk('packing/fetchPackageWeight', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, hostname = _a.hostname;
    var rejectWithValue = _b.rejectWithValue, getState = _b.getState;
    return __awaiter(void 0, void 0, void 0, function () {
        var resolvedHostname, response, scaleComputerId, error_34, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    resolvedHostname = hostname !== null && hostname !== void 0 ? hostname : getPackingQzHostname(getState());
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/weight", {
                            headers: getAuthHeaders(),
                            params: __assign(__assign({}, (shipmentPackageSeqId ? { shipmentPackageSeqId: shipmentPackageSeqId } : {})), (resolvedHostname ? { hostname: resolvedHostname } : {}))
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        scaleComputerId = getScaleComputerIdFromHeaders(response.headers);
                        return [2 /*return*/, {
                                weight: response.data || null,
                                scaleComputerId: scaleComputerId,
                                hostname: resolvedHostname
                            }];
                    }
                    return [2 /*return*/, rejectWithValue('Failed to fetch package weight')];
                case 2:
                    error_34 = _e.sent();
                    errorMsg = ((_d = (_c = error_34.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch package weight';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// cancelShipment
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/cancel
// Service: mantle.shipment.ShipmentServices.cancel#Shipment
// Refactored from: cancelPackage (auth/generatePackingSlip) thunk (the naming is misleading)
// =============================================================================
export var cancelShipment = createAsyncThunk('packing/cancelShipment', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_35, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/cancel", {}, { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        message.success('Shipment cancelled successfully');
                        return [2 /*return*/, response.data];
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_35 = _e.sent();
                    errorMsg = ((_d = (_c = error_35.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to cancel shipment';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// updatePackageContentQuantity
// PUT /rest/s1/handheld/pack/shipment/${shipmentId}/package/${packageSeqId}/content
// Service: mantle.shipment.ShipmentServices.update#ShipmentPackageDetails
// Refactored from: UpdateQuantity (auth/UpdateQuantity) thunk
// =============================================================================
export var updatePackageContentQuantity = createAsyncThunk('packing/updatePackageContentQuantity', function (_a, _b) {
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_36, errorMsg;
        var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, data = __rest(_a, ["shipmentId", "shipmentPackageSeqId"]);
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'updatePackageContentQuantity',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/package/" + shipmentPackageSeqId + "/content", __assign({ shipmentId: shipmentId, shipmentPackageSeqId: shipmentPackageSeqId }, data), { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Quantity updated successfully');
                    return [4 /*yield*/, refreshShipmentAfterMutation(dispatch, shipmentId, 'updatePackageContentQuantity')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'updatePackageContentQuantity',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_36 = _c.sent();
                    errorMsg = extractErrorMessage(error_36, 'Failed to update quantity');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'updatePackageContentQuantity',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchShipmentItemAssets
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/itemAsset
// Service: handheld.shipment.ShipmentServices.get#ShipmentItemAssets
// Refactored from: fetchShipmentItemAssets (auth/fetchShipmentItemAssets) thunk
// =============================================================================
export var fetchShipmentItemAssets = createAsyncThunk('packing/fetchShipmentItemAssets', function (_a, _b) {
    var shipmentId = _a.shipmentId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_37, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/itemAsset", {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.shipmentItemAssetList || []];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_37 = _e.sent();
                    errorMsg = ((_d = (_c = error_37.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch shipment item assets';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchRefundLabel
// GET /rest/s1/handheld/pack/shipment/${shipmentId}/refundLabel
// Service: handheld.shipment.ShipmentServices.void#ShippingLabel
// Refactored from: getRefundLabel (auth/getRefundLabel) thunk
// =============================================================================
export var fetchRefundLabel = createAsyncThunk('packing/getRefundLabel', function (_a, _b) {
    var shipmentId = _a.shipmentId, shipmentPackageSeqId = _a.shipmentPackageSeqId, carrierPartyId = _a.carrierPartyId, shippingGatewayConfigId = _a.shippingGatewayConfigId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, error_38, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/refundLabel?shipmentId=" + shipmentId + "&carrierPartyId=" + carrierPartyId + "&shipmentPackageSeqId=" + shipmentPackageSeqId + "&shippingGatewayConfigId=" + (shippingGatewayConfigId !== null && shippingGatewayConfigId !== void 0 ? shippingGatewayConfigId : ''), { headers: getAuthHeaders() })];
                case 1:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 3];
                    message.success('Refund Successful');
                    return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId }))];
                case 2:
                    _e.sent();
                    return [2 /*return*/, response.data];
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_38 = _e.sent();
                    errorMsg = ((_d = (_c = error_38.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to get refund label';
                    message.error(errorMsg);
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 5: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchFacilities
// GET /rest/s1/fp/facility
// Service: fp.FacilityServices.get#Facilities
// Refactored from: facilitiesList (auth/facilitiesList) thunk
// =============================================================================
export var fetchFacilities = createAsyncThunk('packing/fetchFacilities', function (_, _a) {
    var rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var activeOrgId, response, data, error_39, errorMsg;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    activeOrgId = localStorage.getItem('activeOrgId');
                    return [4 /*yield*/, axios.get('/rest/s1/fp/facility', {
                            params: { organizationPartyId: activeOrgId },
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, (((_b = data.facilityList) === null || _b === void 0 ? void 0 : _b.map(function (item) { return ({
                                key: item.facilityId,
                                value: item.facilityName
                            }); })) || [])];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_39 = _e.sent();
                    errorMsg = ((_d = (_c = error_39.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch facilities';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchEnumeration
// GET /rest/s1/fp/enum?enumTypeId=${enumType}
// Service: utility.UtilityServices.get#EnumerationList
// Refactored from: getEnumerationList (auth/getEnumerationList) thunk
// =============================================================================
export var fetchEnumeration = createAsyncThunk('packing/fetchEnumeration', function (_a, _b) {
    var enumType = _a.enumType;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_40, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/fp/enum?enumTypeId=" + enumType, {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.enumTypeList || []];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_40 = _e.sent();
                    errorMsg = ((_d = (_c = error_40.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch enumeration';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// fetchStatusList
// GET /rest/s1/fp/status?statusTypeId=${statusTypeId}
// Service: utility.UtilityServices.get#StatusList
// Refactored from: getStatusList (auth/getStatusList) thunk
// =============================================================================
export var fetchStatusList = createAsyncThunk('packing/fetchStatusList', function (_a, _b) {
    var statusTypeId = _a.statusTypeId;
    var rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var response, data, error_41, errorMsg;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, axios.get("/rest/s1/fp/status?statusTypeId=" + statusTypeId, {
                            headers: getAuthHeaders()
                        })];
                case 1:
                    response = _e.sent();
                    if (response.status === 200) {
                        data = response.data;
                        return [2 /*return*/, data.statusList || []];
                    }
                    return [2 /*return*/, []];
                case 2:
                    error_41 = _e.sent();
                    errorMsg = ((_d = (_c = error_41.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) || 'Failed to fetch status list';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
// Initialize packing - fetches all reference data
export var initializePacking = createAsyncThunk('packing/initialize', function (_, _a) {
    var dispatch = _a.dispatch, rejectWithValue = _a.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var error_42, errorMsg;
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 2, , 3]);
                    // Fetch all reference data in parallel
                    return [4 /*yield*/, Promise.all([
                            dispatch(fetchFacilities()),
                            dispatch(fetchBoxTypes()),
                            dispatch(fetchWeightUoMs()),
                            dispatch(fetchUserPreferences()),
                            dispatch(fetchStatusList({ statusTypeId: 'Shipment' })),
                            dispatch(fetchEnumeration({ enumType: 'ShipmentType' })),
                            dispatch(fetchEnumeration({ enumType: 'ShipmentMethod' })),
                            dispatch(fetchEnumeration({ enumType: 'FacilityLocationType' })),
                            dispatch(fetchEnumeration({ enumType: 'ContainerType' }))
                        ])];
                case 1:
                    // Fetch all reference data in parallel
                    _d.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_42 = _d.sent();
                    errorMsg = ((_c = (_b = error_42.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.errors) || 'Failed to initialize packing';
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 3: return [2 /*return*/];
            }
        });
    });
});
